'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Mode } from '@/lib/api';
import { loadModeTried, saveModeTried } from '@/lib/languagePrefs';
import { modeHint, modeLabel, type InputLanguage } from '@/lib/modeText';
import ModeIcon from './ModeIcons';

/**
 * The mode that is still new enough to introduce itself.
 *
 * One id, not a list: two buttons waving at once is a fairground, and the point
 * of the animation is that exactly one thing in the column is asking for
 * attention. When the next mode ships, this is the line that moves — and the
 * storage key is per mode (see `loadModeTried`), so the new one gets its own
 * introduction instead of inheriting this one's.
 */
const SPOTLIT = 'ai-prompt';

/**
 * The newest thing on the rail, which is not a mode at all.
 *
 * The attract burst introduces ONE control at a time — two things waving at
 * once is a fairground, which is the note on `SPOTLIT` and still true. So when
 * the Notebook is on the rail and has never been opened, it takes the
 * spotlight; once it has, the burst falls back to whichever mode still wants
 * it. Both are remembered per control (`loadModeTried`), so the reader who has
 * already met one still gets introduced to the other.
 */
const SPOTLIT_EXTRA = 'notebook';

/**
 * How long one burst runs, how long the column stays quiet between them, and
 * how long it waits before the first one.
 *
 * The first numbers here were 6s / 5.2s / 42s and were too polite to work: one
 * wave inside the first ten seconds, then nothing for the better part of a
 * minute, which is easy to sit through without ever seeing it. A hint nobody
 * catches is the same as no hint. So the first burst now lands while the page
 * is still new, and the gap is short enough that anyone reading a translation
 * for a minute sees it at least twice.
 *
 * Still bursts rather than a permanent loop, and still only until the mode has
 * been chosen once — that is what keeps this a hint instead of a nag.
 */
const FIRST_MS = 2500;
const BURST_MS = 5000;
const QUIET_MS = 20000;

/**
 * The mode picker, as a narrow column between the two panes.
 *
 * It sits between input and output because that is where it belongs
 * conceptually — it is the operation applied *between* the two, not a property
 * of either.
 *
 * The buttons are icons, not words. That makes the tooltip load-bearing rather
 * than decorative: an icon-only control that does not say what it is is just a
 * puzzle. So each one carries a custom tooltip with the mode's name on the
 * first line and what it actually does underneath, plus an `aria-label` so the
 * same information reaches a screen reader.
 *
 * Both strings are written with a `{lang}` placeholder in the backend registry
 * and filled in here — the modes that rewrite text inside one language say which
 * language that is, because "A1 English" in front of someone typing Korean is a
 * button that lies about what it does. See lib/modeText.ts.
 */
export default function ModeColumn({
  modes,
  active,
  lang,
  onSelect,
  extras = [],
  numbers = false,
}: {
  modes: Mode[];
  active: string;
  /** What the user is writing in: names the modes, and draws the flag icon. */
  lang: InputLanguage | null;
  onSelect: (id: string) => void;
  /**
   * The buttons on this rail that are NOT modes.
   *
   * The Notebook is the first of them, and the distinction is real rather than
   * cosmetic: every other button here changes what happens to the text in the
   * box, and this one opens a window over it. They are drawn after a divider,
   * they never take the selected fill (nothing is "in notebook mode"), and they
   * keep counting from where the modes stopped — the Notebook is 7 because
   * there are six modes, and it renumbers itself the day a seventh ships rather
   * than needing a constant changed here.
   */
  extras?: {
    id: string;
    label: string;
    hint: string;
    icon: string;
    onClick: () => void;
    /** Drawn lit while its window is open — a state, not a selection. */
    active?: boolean;
  }[];
  /**
   * Put each mode's number on its icon — held up while Ctrl is down.
   *
   * The number is drawn ON the thing it selects rather than on a separate row
   * somewhere else, which is the whole point of it: a list of numbered names
   * off to one side has to be READ and matched back to the column before it is
   * any use, and by then the icons would have been quicker. A badge in the
   * corner of the icon teaches the shortcut for the control you are already
   * looking at, and disappears when you stop asking.
   */
  numbers?: boolean;
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  /**
   * Whether the spotlit mode is mid-burst right now.
   *
   * In BURSTS rather than a steady loop, which is the whole difference between
   * a hint and a nag. A control that never stops moving becomes the thing the
   * eye learns to skip — that is why the games button (globals.css) gives up
   * after eight seconds. But this one cannot simply run once on load and stop:
   * somebody arrives with a paragraph to translate, watches the answer, and
   * never looks at the column at all in the first eight seconds. So it waves
   * for five, waits forty, and waves again — a few times over a session, at
   * moments the reader is likely to be between translations rather than
   * mid-sentence.
   *
   * Starts false so the server-rendered HTML and the first client paint agree;
   * everything that decides otherwise (storage, reduced motion) is
   * browser-only.
   */
  const [attract, setAttract] = useState(false);
  const tried = useRef(false);

  /**
   * Which control the burst is for on THIS render.
   *
   * Computed rather than constant, because it depends on what the reader has
   * already met: the Notebook until it has been opened once, then the spotlit
   * mode until it has been chosen once, then nothing at all, which is where
   * every returning reader ends up.
   *
   * Starts as null on the server and on the first client paint — `loadModeTried`
   * reads localStorage, which neither has — and the effect below is what
   * resolves it. A burst that ran before that would be a burst aimed at the
   * wrong control.
   */
  const [spotlit, setSpotlit] = useState<string | null>(null);

  useEffect(() => {
    const hasExtra = extras.some((x) => x.id === SPOTLIT_EXTRA);
    if (hasExtra && !loadModeTried(SPOTLIT_EXTRA)) { setSpotlit(SPOTLIT_EXTRA); return; }
    if (modes.some((m) => m.id === SPOTLIT) && !loadModeTried(SPOTLIT)) { setSpotlit(SPOTLIT); return; }
    setSpotlit(null);
  }, [modes, extras]);

  useEffect(() => {
    if (!spotlit) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // A self-rescheduling pair rather than setInterval: an interval whose period
    // is the quiet time would fire while a burst was still running after the tab
    // had been backgrounded and the timers had drifted.
    let on: ReturnType<typeof setTimeout>;
    let off: ReturnType<typeof setTimeout>;
    const run = () => {
      if (tried.current) return;
      setAttract(true);
      off = setTimeout(() => {
        setAttract(false);
        on = setTimeout(run, QUIET_MS);
      }, BURST_MS);
    };
    // Not on the very first frame — the page is still assembling itself and a
    // control that is already waving as it appears reads as a loading state
    // rather than as an invitation. A couple of seconds is enough for the
    // column to have settled and for the eye to be somewhere it can be caught.
    on = setTimeout(run, FIRST_MS);
    return () => { clearTimeout(on); clearTimeout(off); };
  }, [spotlit]);

  // Choosing it is the answer to the question the animation was asking, so it
  // stops there and for every future visit — including the visit where they
  // choose it without ever having seen a burst.
  useEffect(() => {
    if (active !== SPOTLIT) return;
    tried.current = true;
    setAttract(false);
    saveModeTried(SPOTLIT);
    setSpotlit((cur) => (cur === SPOTLIT ? null : cur));
  }, [active]);

  /**
   * Opening the Notebook is the answer to the question the burst was asking.
   *
   * Recorded on the CLICK rather than on some later state, because unlike a
   * mode there is nothing about the rail that says afterwards whether it has
   * been opened — the dialog closes and the button goes back to looking exactly
   * as it did. The flag is per control and permanent, so this introduction
   * happens once per browser and never again.
   */
  const met = useCallback((id: string) => {
    if (id !== SPOTLIT_EXTRA) return;
    tried.current = true;
    setAttract(false);
    saveModeTried(SPOTLIT_EXTRA);
    setSpotlit((cur) => (cur === SPOTLIT_EXTRA ? null : cur));
  }, []);

  return (
    // Only as wide as the icons need. It was 132px back when the buttons held
    // words; icon buttons make that mostly empty space between the two panes,
    // which is space the text should have.
    <div
      className="flex shrink-0 flex-row justify-center gap-1.5 border-ink-200 px-2 py-3
                 md:w-16 md:flex-col md:justify-start md:border-x dark:border-ink-800"
    >
      <span className="hidden pb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-ink-400 md:block">
        Mode
      </span>

      {modes.map((m, i) => {
        const on = m.id === active;
        const label = modeLabel(m.label, lang?.name);
        const hint = modeHint(m.hint, lang?.name);
        // Waving right now — and never while it is the mode being used, which
        // the effect above already guarantees but which is cheaper to state
        // here than to reason about.
        const waving = attract && m.id === spotlit && !on;
        return (
          <div
            key={m.id}
            className="relative"
            onMouseEnter={() => setHovered(m.id)}
            onMouseLeave={() => setHovered(null)}
          >
            <button
              type="button"
              aria-pressed={on}
              aria-label={`${label}. ${hint}`}
              onFocus={() => setHovered(m.id)}
              onBlur={() => setHovered(null)}
              onClick={() => onSelect(m.id)}
              className={
                'group flex h-10 w-full items-center justify-center rounded-lg border ' +
                // Snappy rather than smooth: this is a control being pressed,
                // not a page transition — 120ms reads as responsive, anything
                // slower reads as lag. `motion-reduce` drops the movement for
                // anyone who has asked the OS for less animation.
                'transition-all duration-[120ms] ease-out hover:-translate-y-0.5 hover:shadow-md ' +
                'active:translate-y-0 active:scale-95 motion-reduce:transform-none ' +
                (waving ? 'gt-gem-attn ' : '') +
                (on
                  ? 'border-accent-500 bg-accent-600 text-white shadow-sm hover:bg-accent-700'
                  : // Ink, not grey: the column is a row of marks to be read, and
                    // mid-grey made them look disabled. Colour is what the hover
                    // and the selection are FOR — see the note on the wrapper.
                    'border-transparent text-ink-900 hover:border-ink-200 hover:bg-ink-100 ' +
                    'hover:text-accent-600 dark:text-ink-dim dark:hover:border-ink-700 ' +
                    'dark:hover:bg-ink-800 dark:hover:text-accent-400')
              }
            >
              {/*
                Colour is earned, not given.

                Two of these marks carry their own colour — the US flag and the
                red Mistakes badge — and next to four monochrome line icons they
                shouted, turning the column into a row of unrelated stickers.
                Desaturating them at rest puts every mode on the same footing;
                hovering or selecting one brings its colour back, so colour means
                "this one" rather than "this one is special".

                A filter rather than per-icon colour handling, because it works
                on artwork this component knows nothing about: a mode added
                tomorrow with a full-colour SVG behaves correctly with no edit
                here.

                The gem is the exception, and it is exempted by ICON rather than
                by mode id — the rule being expressed is about the drawing, not
                about which mode happens to use it. A diamond has no colour of
                its own; what you look at in one is fire, and a desaturated
                diamond is a drawing of a piece of glass. Keeping it lit at rest
                also does the job the rest of this change is for: it is the
                newest mode, and the one thing in a column of six grey marks
                that the eye lands on without being told to.
              */}
              <span
                className={
                  'flex items-center justify-center transition-[filter] duration-150 ' +
                  (on || m.icon === 'gem' ? 'grayscale-0' : 'grayscale group-hover:grayscale-0')
                }
              >
                <ModeIcon name={m.icon ?? null} label={label} lang={lang} />
              </span>

              {/*
                The fire coming off it, only mid-burst.

                Three sparks rather than the Overview switch's four, on their own
                staggered delays — see `.gt-spark` in globals.css for why things
                that twinkle are never in step. `pointer-events-none` because
                they overhang the button, and a spark swallowing the click would
                break the control at the exact moment the animation finally
                worked.
              */}
              {waving && (
                <span aria-hidden className="pointer-events-none absolute inset-0 z-10">
                  {[
                    // Inside the button's own box, not overhanging it. The
                    // column is 64px wide between two panes that clip, and the
                    // spark that hung off the left edge was being sliced in half
                    // by the border — a half-star reads as a rendering fault,
                    // which is a strange thing for an invitation to look like.
                    { cls: 'left-0.5 top-0.5 h-2.5 w-2.5 text-fuchsia-400', delay: '0s' },
                    { cls: 'right-0.5 top-1 h-2 w-2 text-sky-400', delay: '0.5s' },
                    { cls: 'bottom-0.5 right-2 h-2 w-2 text-violet-400', delay: '1s' },
                  ].map((s, i) => (
                    <svg
                      key={i}
                      viewBox="0 0 24 24"
                      className={`gt-game-star absolute ${s.cls}`}
                      style={{ animationDelay: s.delay }}
                      fill="currentColor"
                    >
                      <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
                    </svg>
                  ))}
                </span>
              )}
              {/*
                The mode's number, bottom-right of its own icon.

                `pointer-events-none` for the reason the sparks above carry it:
                the badge sits over the button it belongs to, and a corner that
                swallowed the click would break the control at the exact spot a
                reader is most likely to aim for.

                Only the first nine get one. A tenth mode would need a two-key
                shortcut, and a badge promising a key that cannot be pressed is
                worse than no badge — see the digit handler in Translator.tsx,
                which reads the same nine.
              */}
              {numbers && i < 9 && (
                <span
                  aria-hidden
                  className={
                    'pointer-events-none absolute bottom-0.5 right-0.5 z-20 flex h-4 w-4 ' +
                    'items-center justify-center rounded text-[10px] font-bold leading-none ' +
                    'tabular-nums shadow-sm ring-1 ' +
                    (on
                      ? 'bg-white text-accent-700 ring-accent-700/20'
                      : 'bg-accent-600 text-white ring-black/10')
                  }
                >
                  {i + 1}
                </span>
              )}
            </button>

            {hovered === m.id && <Tip label={label} hint={hint} />}
          </div>
        );
      })}

      {/*
        The rule between the modes and everything else.

        One hairline, and it is doing real work: without it the Notebook reads
        as a seventh mode, and somebody would click it expecting the text in the
        box to come back rewritten. A gap alone was tried and is not enough on a
        64px rail — at that width a gap looks like spacing, not like a
        boundary. Horizontal on the mobile row, where the rail lies on its side.
      */}
      {extras.length > 0 && (
        <span
          aria-hidden
          className="mx-1 w-px self-stretch bg-ink-200 md:mx-0 md:my-1.5 md:h-px md:w-full dark:bg-ink-700"
        />
      )}

      {extras.map((x, i) => {
        const waving = attract && x.id === spotlit && !x.active;
        return (
        <div
          key={x.id}
          className="relative"
          onMouseEnter={() => setHovered(x.id)}
          onMouseLeave={() => setHovered(null)}
        >
          <button
            type="button"
            aria-label={`${x.label}. ${x.hint}`}
            aria-expanded={x.active ? true : undefined}
            onFocus={() => setHovered(x.id)}
            onBlur={() => setHovered(null)}
            onClick={() => { met(x.id); x.onClick(); }}
            className={
              'group flex h-10 w-full items-center justify-center rounded-lg border ' +
              'transition-all duration-[120ms] ease-out hover:-translate-y-0.5 hover:shadow-md ' +
              'active:translate-y-0 active:scale-95 motion-reduce:transform-none ' +
              (waving ? 'gt-gem-attn ' : '') +
              (x.active
                ? 'border-accent-500 bg-accent-600 text-white shadow-sm hover:bg-accent-700'
                : 'border-transparent text-ink-900 hover:border-ink-200 hover:bg-ink-100 ' +
                  'hover:text-accent-600 dark:text-ink-dim dark:hover:border-ink-700 ' +
                  'dark:hover:bg-ink-800 dark:hover:text-accent-400')
            }
          >
            {/* No grayscale filter here, unlike the modes above: this mark keeps
                its own colours at rest. See the note on `Notebook` in ModeIcons. */}
            <span className="flex items-center justify-center">
              <ModeIcon name={x.icon} label={x.label} lang={lang} />
            </span>

            {/* The same three sparks the spotlit mode gets, on the same staggered
                delays, inside the button's own box so the 64px rail cannot slice
                one in half. `pointer-events-none`, or a spark swallows the click
                at the moment the animation finally worked. */}
            {waving && (
              <span aria-hidden className="pointer-events-none absolute inset-0 z-10">
                {[
                  { cls: 'left-0.5 top-0.5 h-2.5 w-2.5 text-amber-400', delay: '0s' },
                  { cls: 'right-0.5 top-1 h-2 w-2 text-sky-400', delay: '0.5s' },
                  { cls: 'bottom-0.5 right-2 h-2 w-2 text-violet-400', delay: '1s' },
                ].map((sp, k) => (
                  <svg
                    key={k}
                    viewBox="0 0 24 24"
                    className={`gt-game-star absolute ${sp.cls}`}
                    style={{ animationDelay: sp.delay }}
                    fill="currentColor"
                  >
                    <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
                  </svg>
                ))}
              </span>
            )}

            {/* Numbered on from the modes — see the note on `extras`. */}
            {numbers && modes.length + i < 9 && (
              <span
                aria-hidden
                className={
                  'pointer-events-none absolute bottom-0.5 right-0.5 z-20 flex h-4 w-4 ' +
                  'items-center justify-center rounded text-[10px] font-bold leading-none ' +
                  'tabular-nums shadow-sm ring-1 ' +
                  (x.active
                    ? 'bg-white text-accent-700 ring-accent-700/20'
                    : 'bg-accent-600 text-white ring-black/10')
                }
              >
                {modes.length + i + 1}
              </span>
            )}
          </button>

          {hovered === x.id && <Tip label={x.label} hint={x.hint} />}
        </div>
        );
      })}
    </div>
  );
}

/**
 * The hover card an icon-only rail cannot do without.
 *
 * Left of the column on wide screens — the output pane is to the right and a
 * tooltip over it would cover the translation — and below on narrow ones, where
 * the rail is a horizontal row.
 */
function Tip({ label, hint }: { label: string; hint: string }) {
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-full z-40 mt-1.5 w-56
                 -translate-x-1/2 rounded-lg border border-ink-200 bg-white p-2.5
                 text-left shadow-xl md:left-auto md:right-full md:top-0 md:mr-2 md:mt-0
                 md:translate-x-0 dark:border-ink-700 dark:bg-ink-800"
    >
      <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{label}</p>
      <p className="mt-1 text-xs leading-relaxed text-ink-500 dark:text-ink-300">{hint}</p>
    </div>
  );
}
